import React, {
  FC,
  createContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react';

import FilterOptions from 'organisms/Filter/components/FilterOptions';
import DatePickerFilter from 'organisms/Filter/forms/DatePickerFilter';
import MonthPickerFilter from 'organisms/Filter/forms/MonthPickerFilter';
import SingleMonthPickerFilter from 'organisms/Filter/forms/SingleMonthPickerFilter';
import ListFilter from 'organisms/Filter/forms/ListFilter';
import { Popover, IconButton, Stack, Box, Tooltip } from '@mui/material';

import { neutrals } from 'utils/styles/color';
import {
  IFilterContext,
  IFilterOption,
  IFilterProps,
} from 'organisms/Filter/types';

import { ReactComponent as FilterIcon } from 'assets/icons/filter.svg';
import { ReactComponent as CloseIcon } from 'assets/icons/close.svg';
// import loaderGif from 'assets/images/loaderGif.gif';

export const FilterContext = createContext<IFilterContext>({
  filter: {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setFilter: (c) => c,
  options: [],
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setOptions: (o) => o,
});

const Filter: FC<IFilterProps> = ({
  options,
  filterExtend,
  onChange,
  loading,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [filterOptions, setFilterOptions] = useState<IFilterOption[]>([]);
  const [filter, setFilter] = useState({});

  const boxRef = useRef(null);

  const onFilterChange = useCallback(
    (nextFilter: { [key: string]: unknown }) => {
      setFilter((prev) => {
        onChange({ ...prev, ...nextFilter });
        return { ...prev, ...nextFilter };
      });
    },
    []
  );

  useEffect(() => {
    setFilterOptions(options);
  }, [options]);

  useEffect(() => {
    if (!filterExtend) return;
    onFilterChange(filterExtend);
  }, [filterExtend]);

  useEffect(() => {
    onChange(filter);
  }, [filter]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    document.body.setAttribute('id', 'body');
  };

  const handleReset = () => {
    setFilter({});
  };

  const handleClose = () => {
    setAnchorEl(null);
    document.body.setAttribute('id', '');
  };

  const isFilled = (() => {
    if (!filter) return false;
    return !Object.values(filter).every((value) => {
      if (Array.isArray(value) && value.length === 0) return true;
      if (typeof value === 'string' && value === '') return true;
      return value === undefined;
    });
  })();

  const open = Boolean(anchorEl);
  const id = open ? 'filter-popover' : undefined;
  const activeOption = filterOptions.find((o) => o.active);

  // eslint-disable-next-line
  console.log(filter, loading);

  return (
    <FilterContext.Provider
      value={{
        filter,
        setFilter: onFilterChange,
        options: filterOptions,
        setOptions: setFilterOptions,
      }}
    >
      <div>
        <IconButton
          sx={{ p: 0 }}
          onClick={handleClick}
          aria-describedby={id}
          color="primary"
          aria-label="Filter data"
          component="span"
        >
          <FilterIcon />
        </IconButton>
        {isFilled && (
          <Tooltip title="Reset all applied filters">
            <IconButton
              sx={{ p: 0, ml: 1 }}
              onClick={handleReset}
              color="primary"
              aria-label="Reset filter"
              component="span"
            >
              <CloseIcon />
            </IconButton>
          </Tooltip>
        )}
        <Popover
          id={id}
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          container={boxRef.current}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          sx={{
            position: 'unset',
          }}
          PaperProps={{
            'aria-label': 'Filter options',
            sx: {
              overflowY: 'hidden',
              minWidth: {
                xs: '0 !important',
                md: '600px !important',
              },
              width: {
                xs: 'calc(100vw - 32px)',
                md: 600,
              },
              maxWidth: {
                xs: 'calc(100vw - 32px)',
                md: 600,
              },
              height: {
                xs: 'min(600px, calc(100vh - 80px))',
                md: 362,
              },
              minHeight: 362,
            },
          }}
        >
          <Stack
            data-testid="filter-popover-layout"
            sx={{
              height: '100%',
              position: 'relative',
              flexDirection: { xs: 'column', md: 'row' },
            }}
          >
            {/* {loading ? (
              <Box
                sx={{
                  position: 'absolute',
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  background: 'rgba(255, 255, 255, .7)',
                  zIndex: 9,
                }}
              >
                <img src={loaderGif} alt="Data loading" />
              </Box>
            ) : null} */}
            <Box
              data-testid="filter-options-pane"
              sx={{
                width: { xs: '100%', md: 184 },
                maxHeight: { xs: 120, md: 'none' },
                flexShrink: 0,
                overflowY: { xs: 'auto', md: 'visible' },
                borderRight: {
                  xs: 'none',
                  md: `1px solid ${neutrals[100]}`,
                },
                borderBottom: {
                  xs: `1px solid ${neutrals[100]}`,
                  md: 'none',
                },
              }}
            >
              <FilterOptions />
            </Box>
            {/* , opacity: loading ? 0 : 1 */}
            <Box
              data-testid="filter-form-pane"
              sx={{
                width: { xs: '100%', md: 416 },
                minWidth: 0,
                minHeight: 0,
                flex: { xs: '1 1 auto', md: '0 0 auto' },
                overflowY: { xs: 'auto', md: 'visible' },
              }}
            >
              {activeOption?.code === 'invoice_date' && (
                <DatePickerFilter name="invoice_date" />
              )}
              {activeOption?.code === 'completion_date' && (
                <DatePickerFilter name="completion_date" />
              )}
              {activeOption?.code === 'sent_date' && (
                <DatePickerFilter name="sent_date" />
              )}
              {activeOption?.code === 'created_at' && (
                <DatePickerFilter name="created_at" />
              )}
              {activeOption?.code === 'date' && (
                <MonthPickerFilter name="date" />
              )}
              {activeOption?.code === 'google_invoice_date' && (
                <MonthPickerFilter name="google_invoice_date" />
              )}
              {activeOption?.code === 'gcp_billing_month' && (
                <SingleMonthPickerFilter name="gcp_billing_month" />
              )}
              {activeOption?.code === 'invoice_automation' && (
                <ListFilter name="invoice_automation" />
              )}
              {activeOption?.code === 'currency' && (
                <ListFilter
                  name="currency"
                  showSearch={activeOption.showSearch}
                />
              )}
              {activeOption?.code === 'project_ids' && (
                <ListFilter name="project_ids" />
              )}
              {activeOption?.code === 'invoice_type' && (
                <ListFilter name="invoice_type" />
              )}
              {activeOption?.code === 'payment_schedule' && (
                <ListFilter name="payment_schedule" />
              )}
              {activeOption?.code === 'invoice_creation_approval' && (
                <ListFilter name="invoice_creation_approval" />
              )}
              {activeOption?.code === 'contract_type' && (
                <ListFilter name="contract_type" />
              )}
              {activeOption?.code === 'status' && <ListFilter name="status" />}
              {activeOption?.code === 'sales_rep' && (
                <ListFilter name="sales_rep" />
              )}
              {activeOption?.code === 'service_type' && (
                <ListFilter name="service_type" />
              )}
              {activeOption?.code === 'company' && (
                <ListFilter name="company" />
              )}
              {activeOption?.code === 'accountant' && (
                <ListFilter name="accountant" />
              )}
              {activeOption?.code === 'invoice_status' && (
                <ListFilter name="invoice_status" showSearch={false} />
              )}
              {activeOption?.code === 'company_status' && (
                <ListFilter name="company_status" showSearch={false} />
              )}
              {activeOption?.code === 'subject' && (
                <ListFilter name="subject" />
              )}
              {activeOption?.code === 'close_date' && (
                <DatePickerFilter name="close_date" />
              )}
              {activeOption?.code === 'group' && <ListFilter name="group" />}
              {activeOption?.code === 'integrated' && (
                <ListFilter name="integrated" />
              )}
              {activeOption?.code === 'invoice_creation_automation' && (
                <ListFilter name="invoice_creation_automation" />
              )}
              {activeOption?.code === 'billing_data_check' && (
                <ListFilter name="billing_data_check" />
              )}
              {activeOption?.code === 'google_sales_rep_name' && (
                <ListFilter name="google_sales_rep_name" />
              )}
              {activeOption?.code === 'googler_name' && (
                <ListFilter name="googler_name" />
              )}
              {activeOption?.code === 'google_sales_rep_email' && (
                <ListFilter name="google_sales_rep_email" />
              )}
              {activeOption?.code === 'action' && <ListFilter name="action" />}
              {activeOption?.code === 'target_type' && (
                <ListFilter name="target_type" />
              )}
              {activeOption?.code === 'user_id' && (
                <ListFilter name="user_id" />
              )}
            </Box>
          </Stack>
        </Popover>

        {/* Container for menu paper */}
        <Box
          data-testid="filter-popover-host"
          className="filter-popover-container"
          sx={{
            display: open ? 'block' : 'none',
            width: { xs: 'calc(100vw - 32px)', md: 654 },
            height: {
              xs: 'min(600px, calc(100vh - 80px))',
              md: 393,
            },
            maxWidth: 'calc(100vw - 32px)',
            position: 'absolute',
            top: '40px',
            left: '5px',
          }}
        >
          <Box
            ref={boxRef}
            sx={{
              zIndex: 10,
              position: 'relative',
              width: '100%',
              height: '100%',
              '.MuiPaper-root': {
                top: '10px !important',
                left: {
                  xs: '0 !important',
                  md: '14px !important',
                },
              },
            }}
          />
        </Box>
      </div>
    </FilterContext.Provider>
  );
};

export default Filter;
